import React, { useState, ChangeEvent, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import LoadingScreen from '../../components/common/LoadingScreen';
import { useTenantNavigation } from '../../hooks/navigation/useTenantNavigation';
import { getTenantIdFromStorage } from '../../services/authentication/supertokens';
import { FaSearch } from "react-icons/fa";
import {WaiverItem, WaiverItemType} from "@/types/event/WaiverItem";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import { useCreateEvent } from '../../hooks/event/useCreateEvent';
import BackButton from '../../components/common/BackButton';
import { Card, CardContent, CardDescription, CardTitle } from "../../components/ui/card";
import { cn } from "@/lib/utils";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Trash2 } from "lucide-react";

interface TicketTier {
    name: string;
    price: number;
    description: string;
    tickets_available: number;
}

interface BursaryOption {
    amount: number;
    description: string;
}



function CreateEvent() {
    const { getTenantPath } = useTenantNavigation();
    const {
        formData,
        errors,
        loading,
        successMessage,
        handleChange,
        handleSubmit,
        addTicketTier,
        removeTicketTier,
        handleTicketTierChange,
        addBursaryOption,
        removeBursaryOption,
        handleBursaryOptionChange,
        handleWaiverItemChange,
        addWaiverItem,
        removeWaiverItem,
    } = useCreateEvent();







    if (loading) {
        return (
            <LoadingScreen
                isLoading={true}
            >
                <div></div>
            </LoadingScreen>
        );
    }
    return (
        <section className="bg-background">
            <div className="container mx-auto pb-12">
                <div className="mb-0 md:mb-6">
                    <BackButton to={getTenantPath('/dashboard')} text="Back to Dashboard" />
                </div>

                <Card className="border-none bg-[#23d6e4] text-white">
                    <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div>
                                <CardTitle className="text-3xl font-bold mb-2 text-white">
                                    Create Event
                                </CardTitle>
                                <CardDescription className="text-white/80">
                                    Set up a new event for your organization
                                </CardDescription>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="mt-6">
                    <CardContent className="p-6">
                        {successMessage && (
                            <Alert className="mb-6">
                                <AlertDescription>{successMessage}</AlertDescription>
                            </Alert>
                        )}
                        {errors.apiError && (
                            <Alert variant="destructive" className="mb-6">
                                <AlertDescription>{errors.apiError}</AlertDescription>
                            </Alert>
                        )}

                        <form onSubmit={(e) => {
                            e.preventDefault();
                            handleSubmit();
                        }} className="space-y-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                        Title
                                    </label>
                                    <Input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        placeholder="Event Title"
                                        className="h-12 text-lg border-2 focus:border-black"
                                    />
                                    {errors.title && (
                                        <p className="text-destructive text-sm mt-1">{errors.title}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                        Description
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        className="w-full rounded-md border-2 border-input bg-background px-4 py-2 text-sm focus:border-black"
                                        rows={4}
                                        maxLength={5000}
                                    />
                                    <div className="flex justify-between items-center mt-1">
                                        {errors.description && (
                                            <p className="text-destructive text-sm">{errors.description}</p>
                                        )}
                                        <p className="text-muted-foreground text-sm ml-auto">
                                            {5000 - formData.description.length} characters remaining
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Date
                                        </label>
                                        <input
                                            type="date"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleChange}
                                            className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12 [-webkit-appearance:none] [appearance:none]"
                                        />
                                        {errors.date && (
                                            <p className="text-destructive text-sm mt-1">{errors.date}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Location
                                        </label>
                                        <Input
                                            type="text"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleChange}
                                            placeholder="Event Location"
                                            className="h-12 text-lg border-2 focus:border-black"
                                        />
                                        {errors.location && (
                                            <p className="text-destructive text-sm mt-1">{errors.location}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Start Time
                                        </label>
                                        <input
                                            type="time"
                                            name="time_start"
                                            value={formData.time_start}
                                            onChange={handleChange}
                                            className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12 [-webkit-appearance:none] [appearance:none]"
                                        />
                                        {errors.time_start && (
                                            <p className="text-destructive text-sm mt-1">{errors.time_start}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            End Time
                                        </label>
                                        <input
                                            type="time"
                                            name="time_end"
                                            value={formData.time_end}
                                            onChange={handleChange}
                                            className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12 [-webkit-appearance:none] [appearance:none]"
                                        />
                                        {errors.time_end && (
                                            <p className="text-destructive text-sm mt-1">{errors.time_end}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Contact Email
                                        </label>
                                        <Input
                                            type="email"
                                            name="contact_email"
                                            value={formData.contact_email}
                                            onChange={handleChange}
                                            placeholder="Contact Email"
                                            className="h-12 text-lg border-2 focus:border-black"
                                        />
                                        {errors.contact_email && (
                                            <p className="text-destructive text-sm mt-1">{errors.contact_email}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Contact Number
                                        </label>
                                        <Input
                                            type="tel"
                                            name="contact_number"
                                            value={formData.contact_number}
                                            onChange={handleChange}
                                            placeholder="Contact Number"
                                            className="h-12 text-lg border-2 focus:border-black"
                                        />
                                        {errors.contact_number && (
                                            <p className="text-destructive text-sm mt-1">{errors.contact_number}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-xl font-semibold">Ticket Tiers</h3>
                                        <Button 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                addTicketTier();
                                            }}
                                            variant="outline"
                                            className="bg-black text-white hover:bg-black/90 hover:text-white transition-transform duration-200 hover:scale-105"
                                        >
                                            Add Tier
                                        </Button>
                                    </div>
                                    {errors.ticket_tiers && (
                                        <Alert variant="destructive">
                                            <AlertDescription>{errors.ticket_tiers}</AlertDescription>
                                        </Alert>
                                    )}
                                    {formData.ticket_tiers.map((tier, index) => (
                                        <Card key={index} className="bg-muted/50 hover:shadow-md transition-shadow">
                                            <CardContent className="p-4">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h4 className="text-lg font-semibold">Tier {index + 1}</h4>
                                                    {formData.ticket_tiers.length > 1 && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => removeTicketTier(index)}
                                                            className="text-destructive hover:text-destructive/90"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-foreground mb-2">
                                                            Tier Name
                                                        </label>
                                                        <Input
                                                            type="text"
                                                            value={tier.name}
                                                            onChange={(e) => handleTicketTierChange(index, 'name', e.target.value)}
                                                            className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                        />
                                                        {errors[`tier_${index}_name`] && (
                                                            <p className="text-sm text-destructive mt-1">{errors[`tier_${index}_name`]}</p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-foreground mb-2">
                                                            Price (CAD)
                                                        </label>
                                                        <Input
                                                            type="number"
                                                            value={isNaN(tier.price) ? '' : tier.price}
                                                            onChange={(e) => handleTicketTierChange(index, 'price', e.target.value)}
                                                            className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                            min="0"
                                                            step="0.01"
                                                        />
                                                        {errors[`tier_${index}_price`] && (
                                                            <p className="text-sm text-destructive mt-1">{errors[`tier_${index}_price`]}</p>
                                                        )}
                                                    </div>
                                                    <div className="md:col-span-2">
                                                        <label className="block text-sm font-medium text-foreground mb-2">
                                                            Description
                                                        </label>
                                                        <textarea
                                                            value={tier.description}
                                                            onChange={(e) => handleTicketTierChange(index, 'description', e.target.value)}
                                                            className="w-full rounded-md border-2 border-input bg-background px-4 py-2 text-sm focus:border-black"
                                                            rows={2}
                                                        />
                                                        {errors[`tier_${index}_description`] && (
                                                            <p className="text-sm text-destructive mt-1">{errors[`tier_${index}_description`]}</p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-foreground mb-2">
                                                            Total Tickets Available
                                                        </label>
                                                        <Input
                                                            type="number"
                                                            value={isNaN(tier.tickets_available) ? '' : tier.tickets_available}
                                                            onChange={(e) => handleTicketTierChange(index, 'tickets_available', e.target.value)}
                                                            className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                            min="0"
                                                        />
                                                        {errors[`tier_${index}_tickets`] && (
                                                            <p className="text-sm text-destructive mt-1">{errors[`tier_${index}_tickets`]}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-center space-x-2">
                                        <Switch
                                            id="bursary-enabled"
                                            checked={formData.bursary_enabled}
                                            onCheckedChange={(checked) => handleChange({ target: { name: 'bursary_enabled', type: 'checkbox', checked } } as any)}
                                        />
                                        <label htmlFor="bursary-enabled" className="text-sm font-medium text-foreground">
                                            Enable Bursary Requests
                                        </label>
                                    </div>

                                    {formData.bursary_enabled && (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-2">
                                                    Bursary Type
                                                </label>
                                                <select
                                                    name="bursary_type"
                                                    value={formData.bursary_type}
                                                    onChange={handleChange}
                                                    className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                >
                                                    <option value="custom">Let users choose their amount</option>
                                                    <option value="fixed">Provide fixed bursary options</option>
                                                </select>
                                            </div>

                                            {formData.bursary_type === 'fixed' && (
                                                <div className="space-y-4">
                                                    <div className="flex justify-between items-center">
                                                        <h3 className="text-xl font-semibold">Bursary Options</h3>
                                                        <Button 
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                addBursaryOption();
                                                            }}
                                                            variant="outline"
                                                            className="bg-black text-white hover:bg-black/90 hover:text-white transition-transform duration-200 hover:scale-105"
                                                        >
                                                            Add Option
                                                        </Button>
                                                    </div>
                                                    {errors.bursary_options && (
                                                        <Alert variant="destructive">
                                                            <AlertDescription>{errors.bursary_options}</AlertDescription>
                                                        </Alert>
                                                    )}
                                                    {formData.bursary_options.map((option, index) => (
                                                        <Card key={index} className="bg-muted/50 hover:shadow-md transition-shadow">
                                                            <CardContent className="p-4">
                                                                <div className="flex justify-between items-center mb-4">
                                                                    <h4 className="text-lg font-semibold">Option {index + 1}</h4>
                                                                    {formData.bursary_options.length > 1 && (
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => removeBursaryOption(index)}
                                                                            className="text-destructive hover:text-destructive/90"
                                                                        >
                                                                            <Trash2 className="h-4 w-4" />
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-foreground mb-2">
                                                                            Amount (CAD)
                                                                        </label>
                                                                        <input
                                                                            type="number"
                                                                            value={isNaN(option.amount) ? '' : option.amount}
                                                                            onChange={(e) => handleBursaryOptionChange(index, 'amount', e.target.value)}
                                                                            className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                                            placeholder="0.00"
                                                                            min="0"
                                                                            step="0.01"
                                                                        />
                                                                        {errors[`bursary_${index}_amount`] && (
                                                                            <p className="text-sm text-destructive mt-1">{errors[`bursary_${index}_amount`]}</p>
                                                                        )}
                                                                    </div>
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-foreground mb-2">
                                                                            Description
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            value={option.description}
                                                                            onChange={(e) => handleBursaryOptionChange(index, 'description', e.target.value)}
                                                                            className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                                            placeholder=""
                                                                        />
                                                                        {errors[`bursary_${index}_description`] && (
                                                                            <p className="text-sm text-destructive mt-1">{errors[`bursary_${index}_description`]}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </CardContent>
                                                        </Card>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Todo Section */}
                                <div className="space-y-4">
                                    <div className="flex items-center space-x-2">
                                        <Switch
                                            id="waivers-enabled"
                                            checked={formData.waivers_enabled}
                                            onCheckedChange={(checked) => handleChange({ target: { name: 'waivers_enabled', type: 'checkbox', checked } } as any)}
                                        />
                                        <label htmlFor="waivers-enabled" className="text-sm font-medium text-foreground">
                                            Enable Todo/Checklist
                                        </label>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div className="rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800 w-5 h-5 flex items-center justify-center cursor-help text-xs font-medium transition-colors duration-200 border border-gray-300">?</div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Add items that must be completed to attend the event i.e. todo items, acknowledgements, waivers, etc.</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </div>

                                    {formData.waivers_enabled && (
                                        <div className="space-y-4">
                                            <div className="flex justify-between items-center">
                                                <h3 className="text-xl font-semibold">Todo Options</h3>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button 
                                                            variant="outline"
                                                            className="bg-black text-white hover:bg-black/90 hover:text-white transition-transform duration-200 hover:scale-105"
                                                        >
                                                            Add Item
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent>
                                                        <DropdownMenuItem onClick={() => addWaiverItem("link")}>Link</DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => addWaiverItem("checkbox")}>Checkbox</DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                            {errors.waiver_items && (
                                                <Alert variant="destructive">
                                                    <AlertDescription>{errors.waiver_items}</AlertDescription>
                                                </Alert>
                                            )}
                                            {formData.waiver_items.map((item, index) => (
                                                <Card key={item.uuid} className="bg-muted/50 hover:shadow-md transition-shadow">
                                                    <CardContent className="p-4">
                                                        <div className="flex justify-between items-center mb-4">
                                                            <h4 className="text-lg font-semibold">{item.type === "checkbox" ? "Checkbox" : "Link"}</h4>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => removeWaiverItem(index)}
                                                                className="text-destructive hover:text-destructive/90"
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                        {item.type === "checkbox" && (
                                                            <div className="grid grid-cols-1 gap-4">
                                                                <div>
                                                                    <label className="block text-sm font-medium text-foreground mb-2">
                                                                        Message
                                                                    </label>
                                                                    <Input
                                                                        type="text"
                                                                        value={item.message}
                                                                        onChange={(e) => handleWaiverItemChange(index, 'message', e.target.value)}
                                                                        className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                                        placeholder="Enter a message to display with the item"
                                                                    />
                                                                    {errors[`waiver_${index}_message`] && (
                                                                        <p className="text-sm text-destructive mt-1">{errors[`waiver_${index}_message`]}</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {item.type === "link" && (
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                <div>
                                                                    <label className="block text-sm font-medium text-foreground mb-2">
                                                                        Message
                                                                    </label>
                                                                    <Input
                                                                        type="text"
                                                                        value={item.message}
                                                                        onChange={(e) => handleWaiverItemChange(index, 'message', e.target.value)}
                                                                        className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                                        placeholder="Enter a message to display with the item"
                                                                    />
                                                                    {errors[`waiver_${index}_message`] && (
                                                                        <p className="text-sm text-destructive mt-1">{errors[`waiver_${index}_message`]}</p>
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    <label className="block text-sm font-medium text-foreground mb-2">
                                                                        Link
                                                                    </label>
                                                                    <Input
                                                                        type="url"
                                                                        value={item.link}
                                                                        onChange={(e) => handleWaiverItemChange(index, 'link', e.target.value)}
                                                                        className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                                        placeholder="https://example.com"
                                                                        pattern="https?://.+"
                                                                        title="Please enter a valid URL starting with http:// or https://"
                                                                    />
                                                                    {errors[`waiver_${index}_link`] && (
                                                                        <p className="text-sm text-destructive mt-1">{errors[`waiver_${index}_link`]}</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-end gap-4">
                                    <Button
                                        type="submit"
                                        className="w-full md:w-[200px] bg-black text-white hover:bg-black/90 hover:text-white transition-transform duration-200 hover:scale-105"
                                        disabled={loading}
                                    >
                                        {loading ? 'Creating...' : 'Create Event'}
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </section>
    );
}

export default CreateEvent;